// src/oura/screens/Tracker.tsx
//
// The staff Fixes screen: the platform version of the internal GSheet master
// tracker, one per business. Two views of the same plan (D7): grouped by who
// moves next (the default), or by week, the plan as it is scheduled. Both
// read one set of statuses and handlers, keep the same row ids for `?task=`
// links, and open the same fix drawer. The plan's own controls (when it was
// built, settings, refresh, the downloadable timeline) sit above both.
//
// The Overview's Recommendations list is folded in here: a recommendation the
// plan answers is that task's row, and one it does not is listed as not in
// the plan.

import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import {
    ActivityIcon,
    CalendarDaysIcon,
    DownloadIcon,
    RefreshCwIcon,
    SettingsIcon,
    ChevronDownIcon,
    ZapIcon,
    MoreHorizontalIcon,
    ArrowRightIcon,
    Trash2Icon,
    AlertTriangleIcon,
    XIcon,
    WrenchIcon,
} from "lucide-react";
import type { Business, ScoreSnapshot } from "../contract";
import { getAudit, type AuditIssue, type Recommendation } from "../audit";
import {
    getTracker,
    type BusinessTracker,
    type TrackerWeek,
    type TrackerTask,
    type TrackerSuggestion,
    type TaskStatus,
    type TrackerBlock,
    blockLabel,
    blockHeading,
    CURRENT_TEMPLATE_VERSION,
} from "../tracker";
import {
    useTaskStatuses,
    setTaskStatus,
    useTaskPlacement,
    moveTask,
    removeTask,
    makeTrackerScope,
    useWeekExpansion,
    setWeekExpanded,
    useFixesView,
    setFixesView,
    type FixesView,
} from "../trackerState";
import { Tooltip } from "../primitives/Tooltip";
import { useWritesPaused } from "@context/maintenance";
import { ContradictionEvidenceBlock, contradictionEvidence } from "../components/ContradictionEvidence";
import { PlanSettings } from "../components/PlanSettings";
import { ByWhoMovesNext } from "../components/fixes/ByWhoMovesNext";
import { FixDrawer } from "../components/fixes/FixDrawer";
import { ProposedRow } from "../components/fixes/FixRows";
import { AffectedPages, StatusSelect, TargetDate } from "../components/fixes/TaskParts";
import { clientAsksShown, useClientAsks, usePlanActivity } from "../components/fixes/useFixesData";
import {
    OWNER_LABEL,
    checkKeyOfRec,
    currentBlockOf,
    fixesGroupId,
    isResolvedByCrawl,
    joinRecommendations,
    resolveTaskByCheck,
    resolveTaskByHint,
    taskNoteText,
    todayKey,
    whoMovesNext,
    type FixesGroup,
    type MovesNextContext,
    type PlacedTask,
} from "../components/fixes/model";
import { downloadExecutionTimeline } from "../lib/timelineExport";
import { useSnackbar } from "@hook/use-snackbar";
import type { AeoTrackerRebuild } from "@service/aeo";
import { cn } from "../lib/cn";
import { shortDate } from "../lib/oura";
import {
    FOCUS_RING,
    FoldCard,
    FoldScope,
    PageSentence,
    PanelIcon,
    PillButton,
    STAFF_STATUS,
    SURFACE,
    Seg,
    TYPE,
    Tag,
    foldKey,
    readFolded,
    useFoldOpener,
    type SegItem,
} from "@oura/ui";

const VIEWS: SegItem<FixesView>[] = [
    { key: "who", label: "By who moves next" },
    { key: "week", label: "By week" },
];

/** No asks known: the client is waiting on nothing we can name. */
const NO_ASKS: ReadonlySet<string> = new Set();

/** Where this screen's cards and groups keep their folds. */
const FIXES_SCOPE = "staff-fixes";

/** How long a row stays tinted after a deep link or a status change moves it. */
const ARRIVAL_MS = 2400;
const MOVED_MS = 2000;

/**
 * A dropdown menu portaled to document.body and positioned off the trigger's
 * rect. The week tables scroll horizontally (overflow-x-auto), which clips any
 * absolutely positioned child, so a portal is the only placement that can't
 * be cut off. Flips above the trigger near the bottom of the viewport.
 */
function AnchoredMenu({
    anchor,
    width,
    onClose,
    children,
}: {
    anchor: DOMRect | null;
    width: number;
    onClose: () => void;
    children: ReactNode;
}) {
    if (!anchor) return null;
    const left = Math.max(8, Math.min(anchor.right - width, window.innerWidth - width - 8));
    // Flip upward when the trigger sits in the bottom fifth of the viewport.
    const flip = anchor.bottom > window.innerHeight - 180;
    const position = flip
        ? { left, bottom: window.innerHeight - anchor.top + 4 }
        : { left, top: anchor.bottom + 4 };
    return createPortal(
        <div className="fixed inset-0 z-50" onClick={onClose}>
            <div
                className="absolute rounded-xl bg-white py-1 shadow-lg ring-1 ring-black/[0.06]"
                style={{ width, ...position }}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>,
        document.body,
    );
}

/**
 * Per-row actions: move the task to another week, or take it out of the plan.
 * The way to the client's documents is in the fix's drawer, on the client's
 * own tasks only: from a row it went to the same card whatever the row.
 */
function RowMenu({
    week,
    onMove,
    blocks,
    onRemove,
}: {
    week: number;
    onMove: (w: number) => void;
    /** Blocks this plan has, so a task cannot be moved somewhere it would vanish. */
    blocks: number[];
    onRemove: () => void;
}) {
    const [anchor, setAnchor] = useState<DOMRect | null>(null);
    const writesPaused = useWritesPaused();
    return (
        <>
            <button
                disabled={writesPaused}
                onClick={(e) => setAnchor((a) => (a ? null : e.currentTarget.getBoundingClientRect()))}
                aria-label="Row actions"
                className={cn("flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600", FOCUS_RING)}
            >
                <MoreHorizontalIcon className="h-4 w-4" />
            </button>
            <AnchoredMenu anchor={anchor} width={176} onClose={() => setAnchor(null)}>
                {/* Only blocks this plan actually HAS.
                    The backend accepts any of 0-4 on the PATCH and stores it,
                    but `serialize_tracker` builds the response by walking
                    `weeksMeta` — so a task moved into a block the plan has no
                    meta entry for is saved and then never returned again. On a
                    plan generated before Kickoff existed, offering Kickoff loses
                    the task. Rebuilding the plan (ourteam-backend#4497) is what
                    adds the block; until then it is not offered. */}
                {blocks.filter((w) => w !== week).map((w) => (
                    <button
                        key={w}
                        disabled={writesPaused}
                        onClick={() => {
                            onMove(w);
                            setAnchor(null);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-600 hover:bg-gray-50"
                    >
                        <ArrowRightIcon className="h-3.5 w-3.5 text-gray-400" />
                        Move to {blockLabel(w)}
                    </button>
                ))}
                <div className="my-1 border-t border-gray-100" />
                <button
                    disabled={writesPaused}
                    onClick={() => {
                        onRemove();
                        setAnchor(null);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-rose-600 hover:bg-rose-50"
                >
                    <Trash2Icon className="h-3.5 w-3.5" />
                    Remove from plan
                </button>
            </AnchoredMenu>
        </>
    );
}

/**
 * Board view: the SAME tracker tasks the Timeline renders, laid out as one
 * column per week.
 *
 * It previously rendered <ToDo>, which reads `snapshot.actions` — a completely
 * different dataset with a different status vocabulary (`todo | in_progress |
 * done`) from the Tracker's (`not_started | in_progress | blocked | done`). So
 * Board and Timeline sat in one screen showing different work under different
 * labels. Both views now read one source.
 */
// Retained deliberately, unrendered: the Board view was removed on 30 Jul
// because it showed the same tasks as the Timeline, but keeping the component
// makes restoring it a one-line change rather than a rewrite.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function WeekBoard({
    weeks,
    onSetStatus,
    onMove,
    blocks,
    onRemove,
    onOpen,
}: {
    weeks: TrackerWeek[];
    onSetStatus: (t: TrackerTask, s: TaskStatus) => void;
    onMove: (t: TrackerTask, w: number) => void;
    blocks: number[];
    onRemove: (t: TrackerTask) => void;
    /** Open the fix drawer for a task. Absent = cards are not clickable. */
    onOpen?: (t: TrackerTask, week: number) => void;
}) {
    return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {weeks.map((w) => {
                const doneCount = w.tasks.filter((t) => t.status === "done").length;
                return (
                    <div key={w.week} className="flex flex-col rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="mb-1 flex items-baseline justify-between gap-2 px-1">
                            <span className="text-sm font-semibold text-gray-700">{blockLabel(w.week)}</span>
                            <span className="text-xs text-gray-400 tabular-nums">
                                {doneCount}/{w.tasks.length}
                            </span>
                        </div>
                        <p className="mb-3 px-1 text-xs leading-relaxed text-gray-500">{w.title}</p>
                        <div className="flex flex-col gap-2">
                            {w.tasks.length === 0 ? (
                                <p className="px-1 py-6 text-center text-sm text-gray-400">Nothing here.</p>
                            ) : (
                                w.tasks.map((t) => (
                                    <div
                                        key={t.id}
                                        onClick={() => onOpen?.(t, w.week)}
                                        className={cn(
                                            "rounded-md border border-gray-200 bg-white p-3 shadow-sm transition-shadow",
                                            onOpen && "cursor-pointer hover:shadow-md",
                                        )}
                                    >
                                        <p className="text-sm font-medium leading-snug text-gray-900">{t.action}</p>
                                        {t.notes && (
                                            <p className="mt-1 text-xs leading-relaxed text-gray-500">{t.notes}</p>
                                        )}
                                        {/* stopPropagation: the whole card opens the fix drawer, so
                                            changing a status or opening the menu must not also open it. */}
                                        <div
                                            className="mt-2.5 flex items-center justify-between gap-2"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {/* Same control as the Timeline rows, so a status set in
                                                one view is the status in the other. */}
                                            <StatusSelect
                                                value={t.status}
                                                onChange={(s) => onSetStatus(t, s)}
                                                label={`Status of ${t.action}`}
                                            />
                                            <RowMenu
                                                week={w.week}
                                                blocks={blocks}
                                                onMove={(to) => onMove(t, to)}
                                                onRemove={() => onRemove(t)}
                                            />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/**
 * When the plan was last built, and the controls that change it. Shown above
 * both views, because both are the same plan.
 *
 * `generatedAt` is when the PLAN was built, which is not when the scan ran
 * (ourteam-backend#4497). A plan can be rebuilt from the same scan, so the two
 * dates genuinely differ and only one of them answers "is this current?".
 *
 * Falls back to the scan date for a blob stored before the field existed, and
 * says which it is showing rather than presenting one as the other.
 */
function PlanActions({
    tracker,
    capturedAt,
    busy,
    stale,
    onRebuild,
    onOpenSettings,
    onRefreshRecommendations,
    refreshingRecommendations,
    onDownload,
}: {
    tracker: BusinessTracker;
    capturedAt: string;
    busy: boolean;
    stale: boolean;
    onRebuild?: () => void;
    /** Opens the dates-and-phases dialog. A different operation from refresh,
     *  and named so, because both otherwise read as "update the plan". */
    onOpenSettings?: () => void;
    /** Re-reads the recommendations, whose work state the server computes on
     *  read (#3512). Absent hides it. */
    onRefreshRecommendations?: () => void;
    refreshingRecommendations: boolean;
    /** The client facing execution timeline, scoreboard included. */
    onDownload: () => void;
}) {
    const builtAt = tracker.generatedAt;
    return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className={cn(TYPE.tc, "text-gray-500")}>
                {builtAt
                    ? `Plan built ${shortDate(builtAt)}`
                    : capturedAt
                        ? `Built from the ${shortDate(capturedAt)} scan`
                        : "Not built from a scan yet"}
            </span>
            {/* Only shown when the backend says a newer template exists. The
                operator should never have to guess whether rebuilding is worth
                pressing. */}
            {stale && <Tag tone="amber">Update available</Tag>}
            {onOpenSettings && (
                <PillButton variant="text" icon={SettingsIcon} onClick={onOpenSettings}>
                    Plan settings
                </PillButton>
            )}
            {onRefreshRecommendations && (
                <PillButton variant="text" icon={RefreshCwIcon} busy={refreshingRecommendations} onClick={onRefreshRecommendations}>
                    {refreshingRecommendations ? "Refreshing…" : "Refresh recommendations"}
                </PillButton>
            )}
            {onRebuild && (
                <PillButton size="sm" icon={RefreshCwIcon} busy={busy} writes onClick={onRebuild}>
                    {busy ? "Refreshing" : "Refresh plan"}
                </PillButton>
            )}
            <PillButton size="sm" icon={DownloadIcon} onClick={onDownload}>
                Download timeline
            </PillButton>
        </div>
    );
}

/**
 * What a tracker item is actually fixing, on the row itself.
 *
 * The complaint this answers: "the tracker only shows high-level categories, so
 * users still have to go and find the affected pages themselves." A row reading
 * "Add structured data" is a heading. The same row reading "Add structured data
 * : Missing structured data · 6 pages" is a job someone can pick up, and it
 * links to the finding rather than describing it.
 *
 * Two independent sources, in order of trust:
 *
 *   1. `affectedPages` on the task (ourteam-backend#4353). The BE has already
 *      joined the task to its check's still-failing pages. This is the count to
 *      state, because it is the one the fix drawer will act on.
 *   2. the audit check itself, matched on `source.ref`. Older payloads carry no
 *      affectedPages; the label is still worth showing, and `issue.count` is
 *      the same number by construction.
 *
 * A `query`-sourced task returns null on purpose. It came from a question the
 * business is losing, not from a crawl finding, and there is no issue to point
 * at, and inventing a link there would be worse than leaving the row plain.
 */
function fixesFor(
    task: TrackerTask,
    issueByKey: Map<string, AuditIssue>,
): { key: string; label: string; pages: number | null } | null {
    if (task.source?.type !== "check" || !task.source.ref) return null;
    const key = task.source.ref;
    const issue = issueByKey.get(key);
    // `affectedPages` is already filtered to failing pages by the BE, so its
    // length is the live number. `affectedPageCount` is the same value sent
    // alongside; trusted only as far as the array, which is what renders.
    const pages = task.affectedPages
        ? task.affectedPages.length
        : issue?.count ?? null;
    // No label anywhere means the check is not in this payload — say nothing
    // rather than print a raw key like `dup_titles` at the user.
    if (!issue) return null;
    return { key, label: issue.label, pages };
}

/**
 * The "fixing" line under a task's action.
 *
 * A button, not a link, and it stops propagation: the row itself opens the fix
 * drawer, and someone aiming at the issue name means the issue, not the drawer.
 */
function FixesLine({
    fixes,
    onOpenIssue,
}: {
    fixes: { key: string; label: string; pages: number | null };
    onOpenIssue?: (key: string) => void;
}) {
    const body = (
        <>
            <WrenchIcon className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{fixes.label}</span>
            {fixes.pages !== null && (
                <span className="flex-shrink-0 text-gray-400">
                    · {fixes.pages} {fixes.pages === 1 ? "page" : "pages"}
                </span>
            )}
        </>
    );
    if (!onOpenIssue) {
        return (
            <span className="mt-1 flex items-center gap-1 text-xs text-gray-500">{body}</span>
        );
    }
    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onOpenIssue(fixes.key);
            }}
            className={cn("mt-1 flex max-w-full items-center gap-1 rounded text-xs text-blue-600 transition-colors hover:text-blue-700 hover:underline", FOCUS_RING)}
        >
            {body}
        </button>
    );
}

/** The week table's column heads: sentence case, quiet, one size. */
const TH = cn(TYPE.tm, "px-3 py-2 text-left font-medium text-gray-500");

function WeekCard({
    week,
    expanded,
    onToggle,
    highlightTaskId,
    onSetStatus,
    onSetTarget,
    onMove,
    blocks,
    onRemove,
    onOpen,
    issueByKey,
    onOpenIssue,
    suggestions,
    onDecideSuggestion,
}: {
    week: TrackerWeek;
    /** Collapsed weeks keep their whole header (title, goal, dates, done count)
     *  and fold only the task table away (#3399). */
    expanded: boolean;
    onToggle: () => void;
    /** Deep-linked task to emphasise on arrival (#3397); fades, not a selection. */
    highlightTaskId?: string | null;
    onSetStatus: (t: TrackerTask, s: TaskStatus) => void;
    onSetTarget: (t: TrackerTask, date: string) => void;
    onMove: (t: TrackerTask, w: number) => void;
    blocks: number[];
    onRemove: (t: TrackerTask) => void;
    /** Open the fix drawer for a task. Absent = rows are not clickable. */
    onOpen?: (t: TrackerTask, week: number) => void;
    /** This assessment's audit checks by key, so a row can name the finding it
     *  closes. Empty map = no payload to name it from; the line is dropped. */
    issueByKey: Map<string, AuditIssue>;
    /** Open that finding on the Site Audit tab. Absent = the line is text. */
    onOpenIssue?: (issueKey: string) => void;
    /** Proposed work for THIS block (#4513), shown as a blue band above the
     *  tasks. Empty for most weeks. */
    suggestions?: TrackerSuggestion[];
    /** Keep or dismiss a proposal. Absent = fixture, and no band renders. */
    onDecideSuggestion?: (id: string, decision: "keep" | "dismiss") => Promise<boolean>;
}) {
    const done = week.tasks.filter((t) => t.status === "done").length;
    const bodyId = `tracker-week-${week.week}-body`;
    const proposed = (suggestions ?? []).filter((sg) => sg.state === "proposed");
    /**
     * Which rows have their page list open (#3636).
     *
     * Component state, not the tracker store: this is how you left the row
     * looking while you read it, not a fact about the plan, and it should not
     * outlive the visit the way a status does.
     */
    const [openPages, setOpenPages] = useState<Set<string>>(() => new Set());
    return (
        <section className={cn(SURFACE.card, "overflow-hidden")}>
            {/* The whole header row is the accordion trigger: a chevron alone is
                a small target, and the header carries everything a collapsed
                week still needs to say. */}
            <h2 className="m-0">
                <button
                    type="button"
                    onClick={onToggle}
                    aria-expanded={expanded}
                    aria-controls={bodyId}
                    className="flex w-full flex-wrap items-start gap-x-3 gap-y-1 px-5 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600"
                >
                    <PanelIcon icon={CalendarDaysIcon} area="dates" className="mt-0.5" />
                    <span className="min-w-0 flex-1 basis-48">
                        <span className={cn(TYPE.tt, "block text-gray-900")}>{blockHeading(week)}</span>
                        {week.goal && <span className={cn(TYPE.tc, "mt-0.5 block text-gray-500")}>{week.goal}</span>}
                    </span>
                    <span className={cn(TYPE.tc, "flex items-center gap-2 whitespace-nowrap tabular-nums text-gray-500")}>
                        {week.dates ? `${week.dates} · ` : ""}
                        {done} of {week.tasks.length} done
                        <ChevronDownIcon
                            aria-hidden
                            className={cn("h-4 w-4 flex-shrink-0 text-gray-400 transition-transform", expanded && "rotate-180")}
                        />
                    </span>
                </button>
            </h2>

            {/* Proposed work for this block, above the plan and always visible:
                a collapsed week must not hide a decision waiting to be made
                (#4513). Blue, because it is not a task yet; keeping it turns it
                into one of the rows below. */}
            {onDecideSuggestion && proposed.length > 0 && (
                <div className="border-t border-blue-100 bg-blue-50/70 px-5">
                    {proposed.map((sg) => (
                        <ProposedRow key={sg.id} suggestion={sg} onDecide={onDecideSuggestion} />
                    ))}
                </div>
            )}

            {expanded && (
            <div id={bodyId} className="border-t border-gray-100">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-max lg:min-w-0">
                        <thead>
                            <tr className="border-b border-gray-100 bg-gray-50">
                                <th className={cn(TH, "w-8 pl-5")}>#</th>
                                <th className={TH}>Action</th>
                                <th className={cn(TH, "w-px whitespace-nowrap")}>Target</th>
                                <th className={cn(TH, "w-px whitespace-nowrap text-center")}>Owner</th>
                                <th className={cn(TH, "w-px whitespace-nowrap text-center")}>Status</th>
                                <th className="w-px px-4 py-2" aria-label="Actions" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {week.tasks.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-400">
                                        Nothing scheduled this week. Move a task here from another week.
                                    </td>
                                </tr>
                            )}
                            {week.tasks.map((t, i) => {
                                const isDone = t.status === "done";
                                const pageCount = t.affectedPages?.length ?? 0;
                                const pagesOpen = openPages.has(t.id);
                                const issue = t.source?.ref ? issueByKey.get(t.source.ref) : undefined;
                                const note = t.resolved
                                    // A struck row has to say why. "Done" with no
                                    // explanation invites "who marked this done?".
                                    ? "Nothing left to fix. The last scan found no pages with this problem."
                                    : taskNoteText(t, issue);
                                return (
                                    <Fragment key={t.id}>
                                    <tr
                                        id={`tracker-task-${t.id}`}
                                        // Focusable so the deep link can hand focus to
                                        // the row — screen readers announce the arrival
                                        // instead of silently scrolling.
                                        tabIndex={-1}
                                        onClick={() => onOpen?.(t, week.week)}
                                        className={cn(
                                            "transition-colors duration-700 focus-visible:outline-none",
                                            onOpen && "cursor-pointer hover:bg-gray-50",
                                            highlightTaskId === t.id && "bg-blue-100/70 duration-150",
                                        )}
                                    >
                                        <td className="py-3 pl-5 pr-3 text-sm text-gray-400 tabular-nums">{i + 1}</td>
                                        <td className="w-1/2 px-3 py-3">
                                            <div className="flex items-center gap-1.5">
                                                <span className={cn("text-sm font-medium text-gray-900", (isDone || t.resolved) && "text-gray-500 line-through")}>
                                                    {t.action}
                                                </span>
                                                {t.auto && (
                                                    <Tooltip content="Auto tracked. This runs inside Oura, so it completes on its own when the run finishes.">
                                                        <ZapIcon className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" />
                                                    </Tooltip>
                                                )}
                                            </div>
                                            {/* The gate is a fact about the task, not a
                                                field anybody edits, so it costs a whole
                                                column for something read once. Folded in
                                                here, the Action column gets the width back
                                                and the wording stops wrapping. */}
                                            {t.gate && (
                                                <div className="mt-1 text-xs text-gray-400">
                                                    Verification gate: {t.gate}
                                                </div>
                                            )}
                                            {note && <div className="mt-0.5 text-xs leading-relaxed text-gray-500">{note}</div>}
                                            {/* A contradiction carries both quotes, and the
                                                note above is only a sentence ABOUT them. The
                                                whole value of this finding is that a merchant
                                                can verify it themselves, which needs the two
                                                sentences side by side (#3760). Nothing renders
                                                for any other task. */}
                                            {(() => {
                                                const contradiction = contradictionEvidence(t);
                                                return contradiction
                                                    ? <ContradictionEvidenceBlock evidence={contradiction} className="mt-2" />
                                                    : null;
                                            })()}
                                            {/* What this row exists to fix, and how big it is —
                                                so the tracker stops being a list of categories
                                                the reader has to go and size up elsewhere. */}
                                            {(() => {
                                                const fixes = fixesFor(t, issueByKey);
                                                return fixes ? (
                                                    <FixesLine fixes={fixes} onOpenIssue={onOpenIssue} />
                                                ) : null;
                                            })()}
                                            {/* Only where there is a list to show. A task the
                                                BE sent an empty array for has no page-scoped
                                                work, and one it sent nothing for is a payload
                                                that predates #4353 — neither gets a control
                                                that would open on nothing. */}
                                            {pageCount > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenPages((prev) => {
                                                            const next = new Set(prev);
                                                            if (next.has(t.id)) next.delete(t.id); else next.add(t.id);
                                                            return next;
                                                        });
                                                    }}
                                                    aria-expanded={pagesOpen}
                                                    aria-controls={`tracker-pages-${t.id}`}
                                                    className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                                                >
                                                    <ChevronDownIcon className={cn("h-3 w-3 transition-transform", pagesOpen && "rotate-180")} />
                                                    {pagesOpen ? "Hide pages" : `Show ${pageCount} ${pageCount === 1 ? "page" : "pages"}`}
                                                </button>
                                            )}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                            <TargetDate task={t} onSet={(d) => onSetTarget(t, d)} />
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-center">
                                            <Tag tone={t.owner === "ourteam" ? "blue" : "amber"}>{OWNER_LABEL[t.owner]}</Tag>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                            <StatusSelect value={t.status} onChange={(s) => onSetStatus(t, s)} label={`Status of ${t.action}`} />
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                            <RowMenu
                                                week={week.week}
                                                blocks={blocks}
                                                onMove={(w) => onMove(t, w)}
                                                onRemove={() => onRemove(t)}
                                            />
                                        </td>
                                    </tr>
                                    {pagesOpen && (
                                        <tr id={`tracker-pages-${t.id}`}>
                                            <td />
                                            <td colSpan={5} className="px-3 pb-4 pt-1">
                                                <div className="rounded-xl bg-gray-50 p-4">
                                                    <AffectedPages task={t} issue={issue} onOpenIssue={onOpenIssue} />
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {week.deliverable && (
                    <p className={cn(TYPE.tb, "m-0 flex flex-wrap items-center gap-2 border-t border-gray-100 px-5 py-3 text-gray-600")}>
                        <Tag tone="green">Deliverable</Tag>
                        {week.deliverable}
                    </p>
                )}
            </div>
            )}
        </section>
    );
}

export function Tracker({
    snapshot,
    business,
    liveTracker,
    auditIssues,
    recommendations,
    assessmentId,
    onPersist,
    onOpenIssue,
    onOpenQuestions,
    onOpenClient,
    onRefreshRecommendations,
    onRebuild,
    onDecideSuggestion,
    onSaveSettings,
}: {
    snapshot: ScoreSnapshot;
    business: Business;
    /**
     * The real tracker for a live business (ourteam-backend#3686). When absent
     * the fixture generator is used, exactly as before — this screen renders
     * identically either way, it just stops inventing the plan.
     */
    liveTracker?: BusinessTracker;
    /** The same assessment's audit checks (#3400): a task row names the check
     *  it closes and links to it on the Site Audit tab. Absent on a fixture
     *  business means the fixture's own. */
    auditIssues?: AuditIssue[];
    /**
     * The same assessment's recommendations, folded into the plan (D7): each
     * one joins the task that answers it, and the rest are listed as not in
     * the plan. Absent with a live tracker means none were sent; absent on a
     * fixture business means the fixture's own.
     */
    recommendations?: Recommendation[];
    /** The run this plan belongs to, for the activity the fix drawer reads its
     *  impact line from. Absent on a fixture business. */
    assessmentId?: string;
    /**
     * Persist one task change. Called AFTER the local store has already applied
     * it, so the board stays instant and the request is the follow-up rather
     * than the gate — a tracker edit should never wait on a round trip.
     */
    /** Resolves true once the server confirms the write, false if it was rejected
     *  (#3512). Returning the outcome rather than reporting it upstream keeps the
     *  message here, inside SnackbarProvider — the workspace above renders that
     *  provider, so it cannot use the hook itself. */
    onPersist?: (taskId: string, patch: { status?: TaskStatus; week?: number; removed?: boolean; targetDate?: string }) => void | Promise<boolean>;
    /**
     * Open one audit finding on the Site Audit tab.
     *
     * This is the half of the loop that was missing. A tracker item exists
     * BECAUSE a check failed; until now the row could not say which one, let
     * alone take you there, so the plan and the findings read as two unrelated
     * lists. Absent (fixtures) = the row still names its finding, it just
     * doesn't link.
     */
    onOpenIssue?: (issueKey: string) => void;
    /** Open Visibility, where the questions a content fix answers are read. */
    onOpenQuestions?: () => void;
    /**
     * Open the Client screen, on one card when given it. A fix reaches the
     * client through the Google Docs on its Documents card now (YW, 29 Sep
     * 2026): the backend has no "needs the client" status to set. Absent
     * wherever the Client screen is not.
     */
    onOpenClient?: (card?: "documents" | "questions") => void;
    /**
     * Re-read the recommendations (#3512): their work state is computed by the
     * server on read, so only a fresh read moves it. A manual control, apart
     * from the refresh that follows a write. Absent hides it.
     */
    onRefreshRecommendations?: () => Promise<void>;
    /** Rebuild the plan from the stored scan (ourteam-backend#4497). Resolves
     *  with what changed, or null when the call failed. */
    onRebuild?: () => Promise<AeoTrackerRebuild | null>;
    /**
     * Keep or dismiss one proposal from the suggestion queue (#4513).
     *
     * The backend hands back the REGENERATED plan, so the caller replaces its
     * tracker rather than moving the row here — a kept suggestion becomes a real
     * task through the generator, which is what makes a later rebuild reproduce
     * it. Absent (fixture, or no assessment) hides the queue entirely.
     */
    onDecideSuggestion?: (id: string, decision: "keep" | "dismiss") => Promise<boolean>;
    /** Save the plan's dates and phase lengths (#4477, #4517). Resolves true
     *  when the save landed. Absent hides the control entirely. */
    onSaveSettings?: (patch: {
        startDate: string;
        phases: { week: number; weeks: number }[];
    }) => Promise<boolean>;
}) {
    const tracker = useMemo(
        () => liveTracker ?? getTracker(business, snapshot),
        [liveTracker, business, snapshot],
    );
    // A fixture business reads its own audit, as it reads its own plan. A live
    // one reads only what the payload sent: a missing list stays missing.
    const fixtureAudit = useMemo(
        () => (liveTracker ? null : getAudit(business, snapshot)),
        [liveTracker, business, snapshot],
    );
    const recs = recommendations ?? fixtureAudit?.recommendations ?? [];
    const snackbar = useSnackbar();

    // This run's audit checks, by key: the join every task row uses to name
    // the finding it closes (`source.ref` IS a check key).
    const issues = auditIssues ?? fixtureAudit?.issues;
    const issueByKey = useMemo(
        () => new Map((issues ?? []).map((i) => [i.key, i])),
        [issues],
    );

    // Live state: the generated tracker plus this session's updates — statuses
    // (manual or automatic), week moves, and removals. Progress, the banner
    // and the scoreboard all follow.
    // Scoped to business + assessment run: a re-scan starts with a clean sheet.
    const scope = makeTrackerScope(business.id, snapshot.capturedAt);
    const statusOf = useTaskStatuses(scope);
    const placement = useTaskPlacement(scope);
    const view = useFixesView(scope);
    const { asks, settled: asksSettled } = useClientAsks(business.id);
    const today = todayKey();
    const baseTasks = tracker.weeks.flatMap((w) =>
        w.tasks.map((t) => ({
            ...t,
            status: statusOf(t.id, t.status),
            baseWeek: w.week,
            // Carried, not filtered: the row still renders so a task nobody has
            // to do any more is visibly closed rather than missing.
            resolved: isResolvedByCrawl(t),
        })),
    );
    /**
     * Blocks the plan actually has, plus any block a task has been MOVED into.
     *
     * `tracker.weeks` is whatever the backend generated. A plan generated before
     * the Kickoff block existed (ourteam-backend#4476) has weeks 1-4 only, and a
     * tracker is generated once per assessment and stored — so moving a task to
     * Kickoff matched no block and the task silently disappeared from the
     * screen. It was still moved; there was just nowhere to draw it.
     *
     * Synthesizing the block keeps the move honest. The real fix is rebuilding
     * the plan (ourteam-backend#4497), which this does not replace.
     */
    const placedWeeks = new Set(
        baseTasks
            .filter((t) => !placement.isRemoved(t.id))
            .map((t) => placement.weekOf(t.id, t.baseWeek)),
    );
    const blocks: { week: number; title: string; goal: string; deliverable: string; dates: string }[] = [
        ...tracker.weeks,
        ...Array.from(placedWeeks)
            .filter((w) => !tracker.weeks.some((tw) => tw.week === w))
            .map((w) => ({
                week: w,
                title: w === 0 ? "Kickoff" : `Week ${w}`,
                goal: "",
                deliverable: "",
                dates: "",
            })),
    ].sort((x, y) => x.week - y.week);

    const weeks: TrackerWeek[] = blocks.map((w) => ({
        ...w,
        week: w.week as TrackerBlock,
        // Resolved rows sink to the bottom of their block. They stay visible,
        // but they must not push work somebody still has to do further down.
        tasks: baseTasks
            .filter((t) => !placement.isRemoved(t.id) && placement.weekOf(t.id, t.baseWeek) === w.week)
            .sort((x, y) => Number(x.resolved ?? false) - Number(y.resolved ?? false)),
    }));
    const blockNumbers = weeks.map((w) => w.week as number);
    const allTasks = weeks.flatMap((w) => w.tasks);
    /** Every task with the block it sits in now: what the grouped view and the drawer read. */
    const placed: PlacedTask[] = weeks.flatMap((w) => w.tasks.map((t) => ({ ...t, block: w.week })));
    const recJoin = joinRecommendations(recs, placed);
    const doneCount = placed.filter((t) => t.status === "done").length;

    // Proposed suggestions grouped by the block they belong to (#4513), so each
    // WeekCard shows its own as a blue band rather than one list on top. A
    // proposal whose week is not in this plan is pinned to the last block rather
    // than dropped — a decision waiting to be made must not vanish because its
    // suggested week fell outside the current phase layout.
    const proposed = useMemo(
        () => (tracker.suggestions ?? []).filter((sg) => sg.state === "proposed"),
        [tracker.suggestions],
    );
    const suggestionsByWeek = useMemo(() => {
        const map = new Map<number, TrackerSuggestion[]>();
        const present = new Set(weeks.map((w) => w.week as number));
        const lastWeek = weeks.length ? (weeks[weeks.length - 1].week as number) : 0;
        for (const sg of proposed) {
            const wk = present.has(sg.week) ? sg.week : lastWeek;
            const list = map.get(wk);
            if (list) list.push(sg);
            else map.set(wk, [sg]);
        }
        return map;
    }, [proposed, weeks]);
    const currentBlock = currentBlockOf(weeks);
    const ctx: MovesNextContext = { waitingOnClient: asks?.taskIds ?? NO_ASKS, today, currentBlock };
    // The client's documents are on Client, so the way there shows only where Client does.
    const openClient = clientAsksShown ? onOpenClient : undefined;
    const openDocs = openClient ? () => openClient("documents") : undefined;

    // Accordion state (#3399). Only overrides are stored: an untouched week
    // defaults to "current week open, the rest folded", so the page opens on
    // the week the banner above says the user is in.
    //
    // Held in the tracker store rather than `useState`, because this screen is
    // conditionally rendered by the tab switch and therefore unmounts whenever
    // you look at another tab. In component state, a week you expanded folded
    // itself the moment you came back.
    const expansionOf = useWeekExpansion(scope);
    const isWeekExpanded = (w: number) => expansionOf(w, w === currentBlock);
    const expandWeek = (w: number) => {
        if (!isWeekExpanded(w)) setWeekExpanded(scope, w, true);
    };
    const toggleWeek = (w: number) => setWeekExpanded(scope, w, !isWeekExpanded(w));

    // Jump the main column to one week (#3398). Expanding first, scrolling on
    // the next frame — scrolling to a collapsed header both looks broken and
    // lands at the wrong offset once it expands.
    const scrollToWeek = (w: number) => {
        expandWeek(w);
        requestAnimationFrame(() => {
            document
                .getElementById(`tracker-week-${w}`)
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    };

    /**
     * The fix open in the drawer, by id: the row it shows is re-read from the
     * plan on every render, so a status changed in the drawer's foot is the
     * status its sub-line states. A task removed while open closes it.
     */
    const [openTaskId, setOpenTaskId] = useState<string | null>(null);
    const openTask = openTaskId ? placed.find((t) => t.id === openTaskId) : undefined;
    const activity = usePlanActivity(assessmentId, openTask !== undefined);

    // Deep link to one task (#3397), in falling order of certainty:
    //
    //   ?task=<id>          exact and reload-stable — the form the URL is
    //                       rewritten to once anything else resolves.
    //   ?rec=rec_<key>      a Recommendation card. Its check key IS the key the
    //                       task's `source.ref` carries, so this is an id join.
    //   ?task-hint=<text>   the card's title, matched on words. Fixtures only
    //                       now: their rec ids carry no check key.
    //
    // Unresolvable no longer means "land at the top and say nothing". A card
    // that scrolls you to an unchanged page reads as a broken link, and the
    // reader is left hunting for a highlight that was never going to appear —
    // so the reason is stated instead (see `unlinkedRec` below).
    //
    // The week plan expands the task's week and lands on its row. The grouped
    // list lands on the row and opens its drawer, which is what the links
    // into Fixes (Overview's next steps, a Site issue's task) promise.
    const [searchParams, setSearchParams] = useSearchParams();
    const [highlightId, setHighlightId] = useState<string | null>(null);
    /**
     * Open one group of the grouped list, and the Fixes card round it when that
     * was folded, so a link never lands on a row that is not drawn. A group
     * not on screen yet opens when it mounts.
     *
     * For this visit only: a link is not a hand fold, so the next visit starts
     * Not started and Not in the plan folded again (YW, 29 Sep 2026).
     */
    const openFold = useFoldOpener(FIXES_SCOPE);
    const revealGroup = (group: FixesGroup) => {
        if (readFolded(foldKey(FIXES_SCOPE, "fixes"))) openFold("fixes", { persist: false });
        openFold(fixesGroupId(business.id, group), { persist: false });
    };
    /**
     * Tint one row for a moment: emphasis, not a selection, so it fades back
     * out. A second flash replaces the first rather than being cut short by it.
     */
    const flashTimer = useRef<number | undefined>(undefined);
    const flash = (taskId: string, ms: number) => {
        window.clearTimeout(flashTimer.current);
        setHighlightId(taskId);
        flashTimer.current = window.setTimeout(() => setHighlightId(null), ms);
    };
    useEffect(() => () => window.clearTimeout(flashTimer.current), []);
    /** A recommendation that resolved to nothing, named so the banner can say so. */
    const [unlinkedRec, setUnlinkedRec] = useState<string | null>(null);
    const anchorHandled = useRef(false);
    useEffect(() => {
        if (anchorHandled.current) return;
        const byId = searchParams.get("task");
        const recId = searchParams.get("rec");
        const hint = searchParams.get("task-hint");
        if (!byId && !recId && !hint) return;
        // The grouped list sorts a row by the client's questions too: until
        // they load, a task of ours they wait on still reads as Not started,
        // and the link would open that group instead of the row's.
        if (view === "who" && !asksSettled) return;
        anchorHandled.current = true;
        const checkKey = checkKeyOfRec(recId);
        const target = byId
            ? allTasks.find((t) => t.id === byId)
            : (checkKey ? resolveTaskByCheck(allTasks, checkKey) : undefined)
                ?? resolveTaskByHint(allTasks, hint ?? "");
        if (!target) {
            // Only for an arriving recommendation. A bare `?task=` that misses
            // is a stale or hand-edited link, not a gap in the plan, and naming
            // a card the user never clicked would be a lie.
            if (recId || hint) setUnlinkedRec(hint || recId);
            // The card sits under Not in the plan, which starts folded: open it
            // so the reader sees the recommendation the banner names.
            if (view === "who" && recJoin.unplanned.some((r) => r.id === recId || (hint !== null && r.title === hint))) {
                revealGroup("not_in_plan");
            }
            return;
        }
        // `!== undefined`, not truthiness: Kickoff is week 0.
        const targetWeek = weeks.find((w) => w.tasks.some((t) => t.id === target.id))?.week;
        if (view === "week" && targetWeek !== undefined) expandWeek(targetWeek);
        // The grouped list: open the row's group first (Not started starts
        // folded), so the row is drawn before it is scrolled to and focused.
        const at = placed.find((p) => p.id === target.id);
        if (view === "who" && at) revealGroup(whoMovesNext(at, ctx));
        flash(target.id, ARRIVAL_MS);
        if (!byId) setSearchParams({ task: target.id }, { replace: true });
        // Next frame is too early: the expanded week has to lay out first.
        window.setTimeout(() => {
            const el = document.getElementById(`tracker-task-${target.id}`);
            el?.scrollIntoView({ behavior: "smooth", block: "center" });
            // Focus first, so the drawer hands focus back to this row on close.
            el?.focus({ preventScroll: true });
            if (view === "who") setOpenTaskId(target.id);
        }, 80);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, allTasks, weeks, asksSettled]);

    // Completing the right tasks fills the scoreboard's handover column
    // (looked up by id — tasks can move between weeks).
    const doneById = (suffix: string) =>
        allTasks.some((t) => t.id.endsWith(suffix) && t.status === "done");
    const scoreboard = tracker.scoreboard.map((f) => {
        if (f.key === "schema" && doneById("-w1-t6"))
            return { ...f, result: f.result ?? "Markup live on homepage + key templates" };
        if (f.key === "labels" && doneById("-w1-t7"))
            return { ...f, result: f.result ?? "Unique labels on all priority pages" };
        return f;
    });

    // Local store first (instant), server second. The optimistic write is the
    // one the user sees; `onPersist` is a no-op for a fixture business.
    /**
     * The optimistic write stays — a status control that lags a round trip feels
     * broken. What changed (#3512) is that a REJECTED write is now undone and
     * said out loud: the row used to keep showing a status the server never
     * accepted, and the Overview cards silently disagreed with it.
     */
    const onSetStatus = async (t: TrackerTask, s: TaskStatus) => {
        const previous = t.status;
        setTaskStatus(scope, t.id, s);
        // In the grouped list a new status can move the row to another group,
        // out from under the control that changed it. Say where it went and
        // tint it there. The week plan keeps rows in place, so it needs neither.
        const at = placed.find((p) => p.id === t.id);
        if (view === "who" && at) {
            const to = whoMovesNext({ ...at, status: s }, ctx);
            if (to !== whoMovesNext(at, ctx)) {
                const group = STAFF_STATUS[to].label;
                // Not started starts folded: open the group the row lands in,
                // for this visit only, so the tint is seen and the row the
                // drawer hands focus back to is drawn.
                openFold(fixesGroupId(business.id, to), { persist: false });
                snackbar.showSuccess(`Moved to ${group}`, `"${t.action}" now sits under ${group}.`);
                flash(t.id, MOVED_MS);
            }
        }
        const saved = await onPersist?.(t.id, { status: s });
        if (saved === false) {
            setTaskStatus(scope, t.id, previous);
            snackbar.showError(
                "Could not save the status",
                "The change was not saved, so it has been put back. Please try again.",
            );
        }
    };
    const onMove = (t: TrackerTask, w: number) => {
        moveTask(scope, t.id, w);
        onPersist?.(t.id, { week: w });
        const where = blockLabel(w);
        snackbar.showSuccess(`Moved to ${where}`, `"${t.action}" now sits in ${where}.`);
    };
    /** Empty string clears it back to the block's milestone date, which is what
     *  the BE reads `""` as. Optimism is deliberate: the row updates on the next
     *  payload, and a date picker that lags a round trip feels broken. */
    const onSetTarget = (t: TrackerTask, date: string) => {
        onPersist?.(t.id, { targetDate: date });
    };
    const onRemove = (t: TrackerTask) => {
        removeTask(scope, t.id);
        onPersist?.(t.id, { removed: true });
        snackbar.showSuccess("Removed from plan", `"${t.action}" no longer counts toward progress.`);
    };

    const [settingsOpen, setSettingsOpen] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    /**
     * Save the dates, then close.
     *
     * Only closes on success. A dialog that dismisses itself after a failed
     * save tells the operator the dates were accepted, and the plan behind it
     * still shows the old ones.
     */
    const saveSettings = (patch: { startDate: string; phases: { week: number; weeks: number }[] }) => {
        if (!onSaveSettings) return;
        setSavingSettings(true);
        void Promise.resolve(onSaveSettings(patch))
            .then((ok) => {
                if (ok) {
                    setSettingsOpen(false);
                    snackbar.showSuccess("Plan settings saved", "Every date has been recounted in working days.");
                } else {
                    snackbar.showError("Could not save the settings", "The dates are unchanged. Try again.");
                }
            })
            .finally(() => setSavingSettings(false));
    };

    /**
     * Rebuild, with the summary surfaced rather than swallowed.
     *
     * The backend returns exactly what changed. `droppedWithProgress` is the
     * number that matters: everything else the rebuild removes is work nobody
     * had touched, but a dropped task somebody had already started is a thing
     * the operator needs told, not a silent tidy-up.
     */
    const [rebuilding, setRebuilding] = useState(false);
    const planIsStale =
        tracker.templateVersion !== undefined
        && CURRENT_TEMPLATE_VERSION > tracker.templateVersion;
    const runRebuild = () => {
        if (!onRebuild || rebuilding) return;
        setRebuilding(true);
        void Promise.resolve(onRebuild())
            .then((res) => {
                if (!res) {
                    snackbar.showError("Could not refresh the plan", "Nothing was changed. Try again.");
                    return;
                }
                const { tasksAdded, tasksDropped, progressInherited, droppedWithProgress } = res.rebuilt;
                // "Same scan" first, because the obvious reading of "6 tasks
                // added" is that the site got worse — and a refresh cannot
                // discover that. Nothing was re-crawled. The plan changed
                // because the template did, so the copy has to say so before it
                // says a number, or it takes credit for work nobody did and
                // blame for decline that never happened.
                snackbar.showSuccess(
                    "Plan refreshed",
                    "Same scan, newer plan. "
                    + `${tasksAdded} task${tasksAdded === 1 ? "" : "s"} added that did not exist when this plan was made, `
                    + `${tasksDropped} dropped because the scan shows nothing to fix, `
                    + `${progressInherited} kept their progress.`
                    + (droppedWithProgress
                        ? ` ${droppedWithProgress} of the dropped had work recorded against them.`
                        : ""),
                );
            })
            .finally(() => setRebuilding(false));
    };

    const onDownload = () => {
        downloadExecutionTimeline(business, { ...tracker, weeks, scoreboard });
        snackbar.showSuccess(
            "Execution timeline downloaded",
            "The client facing doc. Print to PDF from the browser to send it out.",
        );
    };

    /**
     * Re-read the recommendations (#3512), with progress that tracks the read
     * itself rather than a spinner that flashes for a frame.
     */
    const [refreshingRecs, setRefreshingRecs] = useState(false);
    const refreshRecommendations = () => {
        if (!onRefreshRecommendations || refreshingRecs) return;
        setRefreshingRecs(true);
        onRefreshRecommendations()
            .catch(() => snackbar.showError("Could not refresh the recommendations", "The list is unchanged. Try again."))
            .finally(() => setRefreshingRecs(false));
    };

    const openFix = (t: TrackerTask) => setOpenTaskId(t.id);
    /** Close the drawer, and drop `?task=` so a reload does not open it again. */
    const closeFix = () => {
        setOpenTaskId(null);
        if (!searchParams.has("task")) return;
        const next = new URLSearchParams(searchParams);
        next.delete("task");
        setSearchParams(next, { replace: true });
    };
    const issueOf = (t: TrackerTask) => (t.source?.type === "check" && t.source.ref ? issueByKey.get(t.source.ref) : undefined);

    return (
        <FoldScope value={FIXES_SCOPE}>
            <PageSentence>
                {placed.length - doneCount} open, {doneCount} done. Change a status and Overview follows.
            </PageSentence>

            <div className="space-y-5">
                {/* Arrived from a Recommendation with nothing to land on.
                    Amber, not rose: the plan is missing a step, which is a gap to
                    close, not an error the reader caused. It says which card sent
                    them and what that means, because the alternative (scrolling a
                    page that looks identical to how they left it) reads as a dead
                    link and teaches them not to click the cards. */}
                {unlinkedRec && (
                    <div className="flex items-start gap-2.5 rounded-2xl bg-amber-50 px-4 py-3 ring-1 ring-inset ring-amber-200">
                        <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                        <div className="min-w-0 flex-1">
                            <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>
                                No step in the plan for &ldquo;{unlinkedRec}&rdquo;
                            </p>
                            <p className={cn(TYPE.tb, "m-0 mt-0.5 text-gray-600")}>
                                The site audit raised it, but the plan has no step that closes it.
                            </p>
                        </div>
                        <button
                            onClick={() => setUnlinkedRec(null)}
                            className={cn("flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-amber-700 transition-colors hover:bg-amber-100", FOCUS_RING)}
                            aria-label="Dismiss"
                        >
                            <XIcon className="h-3.5 w-3.5" />
                        </button>
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <Seg ariaLabel="Arrange fixes" items={VIEWS} value={view} onChange={(v) => setFixesView(scope, v)} />
                    <PlanActions
                        tracker={tracker}
                        capturedAt={snapshot.capturedAt}
                        busy={rebuilding}
                        stale={planIsStale}
                        onRebuild={onRebuild ? runRebuild : undefined}
                        onOpenSettings={onSaveSettings ? () => setSettingsOpen(true) : undefined}
                        // Only with something to refresh: a plan with no
                        // recommendations has no list to re-read.
                        onRefreshRecommendations={onRefreshRecommendations && recs.length > 0 ? refreshRecommendations : undefined}
                        refreshingRecommendations={refreshingRecs}
                        onDownload={onDownload}
                    />
                </div>

                {view === "who" ? (
                    <ByWhoMovesNext
                        businessId={business.id}
                        tasks={placed}
                        ctx={ctx}
                        asks={asks}
                        recJoin={recJoin}
                        suggestions={proposed}
                        issueByKey={issueByKey}
                        highlightId={highlightId}
                        onOpen={openFix}
                        onSetStatus={onSetStatus}
                        onDecideSuggestion={onDecideSuggestion}
                        onRebuild={onRebuild ? runRebuild : undefined}
                        rebuilding={rebuilding}
                        onOpenIssue={onOpenIssue}
                        onOpenClient={openClient}
                    />
                ) : (
                    // Progress rail on the left, the work itself on the right.
                    // Side by side, the rail stays in view (sticky) while the
                    // weeks scroll past it. The Scoreboard is not here: a
                    // handover reporting artefact, it ships in the downloadable
                    // execution timeline, which is what it was built for.
                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[260px_minmax(0,1fr)] lg:items-start">
                        <FoldCard
                            id="week-progress"
                            title="Live progress"
                            icon={{ icon: ActivityIcon, area: "work" }}
                            flush
                            className="lg:sticky lg:top-4"
                        >
                            {weeks.map((w) => {
                                const done = w.tasks.filter((t) => t.status === "done").length;
                                return (
                                    // A row is a jump, not a report (#3398): the rail
                                    // is sticky and permanently on screen, so every
                                    // row doubles as navigation to its week section.
                                    <button
                                        key={w.week}
                                        type="button"
                                        onClick={() => scrollToWeek(w.week)}
                                        aria-label={`Go to ${blockHeading(w)}`}
                                        className="flex w-full items-start justify-between gap-2 border-t border-gray-100 px-5 py-3 text-left transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600"
                                    >
                                        <span
                                            className={cn(
                                                // min-w-0 so a long "Content · Weeks 4 to 5"
                                                // wraps inside its own column instead of
                                                // running into the count on its right.
                                                TYPE.tc,
                                                "min-w-0",
                                                w.week === currentBlock ? "font-semibold text-gray-900" : "text-gray-600",
                                            )}
                                        >
                                            {blockHeading(w)}
                                        </span>
                                        <span className={cn(TYPE.tc, "shrink-0 tabular-nums", done === w.tasks.length ? "text-emerald-700" : "text-gray-500")}>
                                            {done} of {w.tasks.length}
                                        </span>
                                    </button>
                                );
                            })}
                        </FoldCard>

                        <div className="min-w-0 space-y-4">
                            {weeks.map((w) => (
                                // The id lives on a wrapper, and scroll-mt keeps
                                // the jumped-to header clear of the viewport edge.
                                <div key={w.week} id={`tracker-week-${w.week}`} className="scroll-mt-4">
                                    <WeekCard
                                        week={w}
                                        blocks={blockNumbers}
                                        expanded={isWeekExpanded(w.week)}
                                        onToggle={() => toggleWeek(w.week)}
                                        highlightTaskId={highlightId}
                                        onSetStatus={onSetStatus}
                                        onSetTarget={onSetTarget}
                                        onMove={onMove}
                                        onRemove={onRemove}
                                        onOpen={openFix}
                                        issueByKey={issueByKey}
                                        onOpenIssue={onOpenIssue}
                                        suggestions={suggestionsByWeek.get(w.week) ?? []}
                                        onDecideSuggestion={onDecideSuggestion}
                                    />
                                </div>
                            ))}

                            {/* The "Detected issues to fix register" table used to sit
                                here. Removed: it restated the Site Audit tab's Issues
                                list from a second source (`fixRegister`) and the two
                                disagreed: different counts, different page sets, no way
                                to tell which was right. Site Audit owns findings; this
                                tab owns the plan. A task already names the finding it
                                closes and links to it, which is the join this table was
                                trying to be. */}
                        </div>
                    </div>
                )}
            </div>

            {openTask && (
                <FixDrawer
                    key={openTask.id}
                    task={openTask}
                    group={whoMovesNext(openTask, ctx)}
                    issue={issueOf(openTask)}
                    rec={recJoin.byTask.get(openTask.id)}
                    activity={activity}
                    today={today}
                    onClose={closeFix}
                    onSetStatus={onSetStatus}
                    onSetTarget={onSetTarget}
                    onOpenIssue={onOpenIssue}
                    onOpenQuestions={onOpenQuestions}
                    // Only on the client's own open tasks: the documents are
                    // where the client is asked, and ours never go there.
                    onOpenDocs={openDocs && openTask.owner === "client" && openTask.status !== "done" ? openDocs : undefined}
                />
            )}

            {settingsOpen && (
                <PlanSettings
                    tracker={tracker}
                    saving={savingSettings}
                    onSave={saveSettings}
                    onClose={() => setSettingsOpen(false)}
                />
            )}
        </FoldScope>
    );
}
